import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const mockFeedback = {
  score: 42,
  summary: [
    { type: 'bad', text: 'Used generic objective statement ("Seeking a challenging role...").' },
    { type: 'good', text: 'Replace with a 2-sentence Professional Summary highlighting your top 3 measurable achievements.' }
  ],
  experience: [
    { type: 'bad', text: 'Bullet points are task-oriented ("Responsible for managing...").' },
    { type: 'good', text: 'Use the XYZ format: "Accomplished [X] as measured by [Y], by doing [Z]."' },
    { type: 'good', text: 'Quantify everything. Add specific numbers, percentages, or dollar amounts to at least 60% of your bullets.' }
  ],
  formatting: [
    { type: 'bad', text: 'Inconsistent date formatting confuses ATS parsers.' },
    { type: 'good', text: 'Use standard "Month Year - Month Year" format.' },
    { type: 'good', text: 'Switch to a clean, ATS-friendly font like Garamond, Arial, or Calibri.' }
  ]
};

const SectionCard = ({ title, icon, items }) => (
  <div className="glass-panel p-6 rounded-2xl">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 bg-slate-800 rounded-lg text-emerald-400">
        <SafeIcon icon={icon} className="w-5 h-5" />
      </div>
      <h3 className="text-lg font-bold text-white">{title}</h3>
    </div>
    <ul className="space-y-3">
      {items.map((item, idx) => (
        <li key={idx} className="flex gap-3 text-sm">
          <SafeIcon 
            icon={item.type === 'good' ? 'CheckCircle' : 'XCircle'} 
            className={`w-5 h-5 flex-shrink-0 mt-0.5 ${item.type === 'good' ? 'text-emerald-500' : 'text-rose-500'}`} 
          />
          <span className={item.type === 'good' ? 'text-slate-300' : 'text-slate-500 line-through'}>
            {item.text}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

export default function SeriousView({ onReset }) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-4xl mx-auto"
    >
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Professional <span className="text-gradient-serious">Analysis</span>
          </h2>
          <p className="text-slate-400">Here is your actionable plan to fix your resume.</p>
        </div>
        
        <div className="flex items-center gap-4 glass-panel px-6 py-4 rounded-2xl w-full md:w-auto">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
              <path
                className="text-slate-800"
                strokeWidth="3"
                stroke="currentColor"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="text-emerald-500"
                strokeWidth="3"
                strokeDasharray={`${mockFeedback.score}, 100`}
                stroke="currentColor"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <span className="absolute text-xl font-bold text-white">{mockFeedback.score}</span>
          </div>
          <div>
            <div className="text-sm text-slate-400 font-medium uppercase tracking-wider">ATS Score</div>
            <div className="text-rose-400 text-sm font-semibold">Critical Fixes Needed</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <SectionCard title="Summary & Objective" icon="Target" items={mockFeedback.summary} />
        <SectionCard title="Formatting & Layout" icon="Layout" items={mockFeedback.formatting} />
        <div className="md:col-span-2">
          <SectionCard title="Experience & Impact" icon="Briefcase" items={mockFeedback.experience} />
        </div>
      </div>

      <div className="flex justify-center mt-12">
        <button 
          onClick={onReset}
          className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 text-sm font-medium"
        >
          <SafeIcon icon="RefreshCw" className="w-4 h-4" />
          Roast Another Resume
        </button>
      </div>
    </motion.div>
  );
}